import React, { useEffect, useState, useImperativeHandle } from "react";
import { REIButton, REIInputNumber } from "../../../../components/ui";
import { IAnalysedProperty, IPropertyPurchaseCost } from "../../../../interface/addAnalyser";
import { useAnalysedPropertyContext } from "./AnalysedPropertyContext";
import { RadioButton } from "primereact/radiobutton";
import { Slider } from "primereact/slider";
import { redirectToRPData, isPropertyDetailValid } from "../../../../utils/commonUtil";

interface PurchaseDetailsProps {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
}

export const PurchaseDetails = React.forwardRef<{ validate: () => boolean }, PurchaseDetailsProps>(
  ({ setIsValid, updateProperty }, ref) => {
    const { analysedProperty, userProfile } = useAnalysedPropertyContext();
    const [loanPurchaseCost, setLoanPurchaseCost] = useState<IPropertyPurchaseCost>();
    const [cashDeposit, setCashDeposit] = useState<number>(0);
    const [cashDepositPercentage, setCashDepositPercentage] = useState<number>(0);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [reset, setReset] = useState<boolean>(false);

    useEffect(() => {
      if (analysedProperty && !reset) {
        setLoanPurchaseCost(analysedProperty.LoanTypeCost.PropertyPurchaseCost);
        if (
          !analysedProperty.LoanTypeCost.PropertyPurchaseCost.isEnterPercentage ||
          analysedProperty.LoanTypeCost.PropertyPurchaseCost.isEnterPercentage === "1"
        ) {
          setCashDepositPercentage(analysedProperty.LoanTypeCost.PropertyPurchaseCost.CashDeposit || 0);
        } else {
          setCashDeposit(analysedProperty.LoanTypeCost.PropertyPurchaseCost.CashDeposit || 0);
        }
        setReset(true);
      }
    }, [analysedProperty]);

    useEffect(() => {
      if (analysedProperty && analysedProperty?.LoanTypeCost?.PropertyOtherPurchaseExpense.StampDuty) {
        setValues("PurchaseAmount", analysedProperty.LoanTypeCost.PropertyPurchaseCost.PurchaseAmount || 0);
      }
    }, [analysedProperty?.LoanTypeCost?.PropertyOtherPurchaseExpense.StampDuty]);

    useEffect(() => {
      if (
        analysedProperty?.PropertyDetail.PropertyDetailId === 0 &&
        analysedProperty?.PropertyDetail.useDefault == "1" &&
        userProfile
      ) {
        setLoanPurchaseCost({
          isEnterPercentage: userProfile.LoanDeposit.EnterPercentage || "1",
          isEnterValue: userProfile.LoanDeposit.EnterValue || "0",
          CashDeposit:
            userProfile.LoanDeposit.EnterPercentage == "1"
              ? userProfile.LoanDeposit.CashDepositPercentage
              : userProfile.LoanDeposit.CashDeposit,
          EquityDeposit:
            userProfile.LoanDeposit.EnterPercentage == "1"
              ? userProfile.LoanDeposit.EquityDepositPercentage
              : userProfile.LoanDeposit.EquityDeposit || 0,
          MarketValueNow: analysedProperty.LoanTypeCost.PropertyPurchaseCost.MarketValueNow || 0,
          PurchaseAmount: analysedProperty.LoanTypeCost.PropertyPurchaseCost.PurchaseAmount || 0,
        });
        setCashDeposit(userProfile.LoanDeposit.EnterValue == "1" ? userProfile.LoanDeposit.CashDeposit : 0);
        setCashDepositPercentage(
          userProfile.LoanDeposit.EnterPercentage == "1" ? userProfile.LoanDeposit.CashDepositPercentage : 0
        );
      }
    }, [analysedProperty?.PropertyDetail.useDefault, userProfile]);

    const setValues = (name: string, value: string | number) => {
      setLoanPurchaseCost((prevState) => {
        if (!prevState) return undefined;

        return {
          ...prevState,
          [name]: value !== undefined ? value : 0,
        };
      });
    };

    const validate = (triggeredByUser = false) => {
      const newErrors: { [key: string]: string } = {};
      if (triggeredByUser) {
        if (!loanPurchaseCost?.PurchaseAmount || loanPurchaseCost.PurchaseAmount == 0) {
          newErrors["PurchaseAmount"] = "Purchase amount must be greater than 0.";
        }
        if (!loanPurchaseCost?.MarketValueNow || loanPurchaseCost.MarketValueNow == 0) {
          newErrors["MarketValueNow"] = "Market value must be greater than 0.";
        }
      }
      setErrors(newErrors);
      const isValid = Object.keys(newErrors).length === 0;
      setIsValid(isValid);
      return isValid;
    };

    useImperativeHandle(ref, () => ({
      validate: () => validate(true),
    }));

    useEffect(() => {
      if (loanPurchaseCost && validate()) {
        updateProperty({
          LoanTypeCost: {
            ...analysedProperty?.LoanTypeCost,
            PropertyPurchaseCost: loanPurchaseCost,
            Loans: analysedProperty?.LoanTypeCost.Loans || [],
            PropertyOtherPurchaseExpense: analysedProperty?.LoanTypeCost.PropertyOtherPurchaseExpense || {
              CapitalInvestment: 0,
              OtherCost: 0,
              SolicitorsCost: 0,
              StampDuty: 0,
            },
          },
        });
      }
    }, [loanPurchaseCost]);

    const calculateTotal = () => {
      if (loanPurchaseCost?.isEnterPercentage == "1") {
        if (loanPurchaseCost?.PurchaseAmount !== undefined) {
          setCashDepositPercentage((loanPurchaseCost.PurchaseAmount * cashDeposit) / 100);
        }
      } else {
        setCashDeposit(analysedProperty?.LoanTypeCost?.PropertyPurchaseCost?.CashDeposit ?? 0);
      }
    };

    return (
      <div className="w-full">
        <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
          <div className="default-profile-header-container">
            <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Purchase details</h3>
          </div>
          {loanPurchaseCost && (
            <div className="mt-4">
              <div className="flex mb-3">
                <div className="flex w-1/3">
                  <RadioButton
                    inputId="purachaseValueinDollar"
                    data-testid="purd-rdo-entervalue"
                    name="purchasevalue"
                    value={loanPurchaseCost.isEnterValue}
                    onChange={(e) => {
                      setValues("isEnterValue", e.checked ? "1" : "0");
                      setValues("isEnterPercentage", e.checked ? "0" : "1");
                      calculateTotal();
                    }}
                    checked={loanPurchaseCost.isEnterValue === "1"}
                  />
                  <label htmlFor="purachaseValueinDollar" className="ml-2">
                    Enter as $ value {isPropertyDetailValid(analysedProperty) ? "" : "*"}
                  </label>
                </div>
                <div className="flex w-1/3 items-center">
                  <RadioButton
                    inputId="purachaseValueinPercentage"
                    data-testid="purd-rdo-enterpercentage"
                    name="purchasevalue"
                    value={loanPurchaseCost.isEnterPercentage}
                    onChange={(e) => {
                      setValues("isEnterPercentage", e.checked ? "1" : "0");
                      setValues("isEnterValue", e.checked ? "0" : "1");
                      calculateTotal();
                    }}
                    checked={loanPurchaseCost.isEnterPercentage === "1"}
                  />
                  <label htmlFor="purachaseValueinPercentage" className="ml-2">
                    Enter as % of property purchase price {isPropertyDetailValid(analysedProperty) ? "" : "*"}
                  </label>
                  <REIButton
                    id="purd-btn-estimatevalue"
                    label="Estimate value"
                    classNames="p-button-link"
                    onClick={() => {
                      redirectToRPData();
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col lg:flex-row lg:w-full gap-2">
                <div className="flex-0 flex-col w-full lg:w-1/2 gap-2">
                  <div className="flex w-full p-1">
                    <div className="w-[20%] mt-2">
                      Deposit ({loanPurchaseCost.isEnterValue === "1" ? "$" : "%"}){" "}
                      {isPropertyDetailValid(analysedProperty) ? "" : "*"}
                    </div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="purd-sldr-deposit"
                        data-testid="purd-sldr-deposit"
                        value={
                          loanPurchaseCost.isEnterValue == "1"
                            ? cashDeposit < 1000000
                              ? cashDeposit
                              : 1000000
                            : cashDepositPercentage
                        }
                        onChange={(e) => {
                          loanPurchaseCost.isEnterValue === "1"
                            ? setCashDeposit(Array.isArray(e.value) ? e.value[0] : e.value)
                            : setCashDepositPercentage(Array.isArray(e.value) ? e.value[0] : e.value);
                          setValues("CashDeposit", Array.isArray(e.value) ? e.value[0] : e.value);
                        }}
                        className="w-full mt-4"
                        min={0}
                        max={loanPurchaseCost.isEnterValue === "1" ? 1000000 : 100}
                      />
                    </div>
                    <div className="w-[40%] flex px-2">
                      <REIInputNumber
                        id="purd-num-deposit"
                        value={loanPurchaseCost.isEnterValue === "1" ? cashDeposit : cashDepositPercentage}
                        onValueChange={(e) => {
                          loanPurchaseCost.isEnterValue === "1"
                            ? setCashDeposit(e.target.value)
                            : setCashDepositPercentage(e.target.value);
                          setValues("CashDeposit", Array.isArray(e.value) ? e.value[0] : e.value);
                        }}
                        className="w-full"
                        useGrouping={true}
                        max={loanPurchaseCost.isEnterValue === "1" ? undefined : 100}
                        maxFractionDigits={loanPurchaseCost.isEnterValue === "1" ? 0 : 2}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-0 flex-col w-full lg:w-1/2 gap-2">
                  <div className="flex w-full p-1">
                    <div className="w-[20%] mt-2">Market value now ($)</div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="purd-sldr-marketvalue"
                        data-testid="purd-sldr-marketvalue"
                        value={loanPurchaseCost?.MarketValueNow > 1500000 ? 1500000 : loanPurchaseCost?.MarketValueNow}
                        onChange={(e) => setValues("MarketValueNow", Array.isArray(e.value) ? e.value[0] : e.value)}
                        className="w-full mt-4"
                        min={0}
                        max={1500000}
                        step={10000}
                      />
                    </div>
                    <div className="w-[40%] flex px-2">
                      <REIInputNumber
                        id="purd-num-marketvalue"
                        value={loanPurchaseCost.MarketValueNow}
                        onValueChange={(e) => setValues("MarketValueNow", e.target.value)}
                        className="w-full"
                        useGrouping={true}
                        invalid={!!errors.MarketValueNow}
                      />
                    </div>
                  </div>
                  {errors.MarketValueNow && <div className="text-red-500 text-sm mt-1">{errors.MarketValueNow}</div>}
                </div>
              </div>
              <div className="flex flex-col lg:flex-row lg:w-full gap-2">
                <div className="flex-0 flex-col w-full lg:w-1/2 gap-2">
                  <div className="flex w-full p-1">
                    <div className="w-[20%] mt-2">Purchase amount ($)</div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="purd-sldr-purchaseamount"
                        data-testid="purd-sldr-purchaseamount"
                        value={loanPurchaseCost?.PurchaseAmount > 1500000 ? 1500000 : loanPurchaseCost?.PurchaseAmount}
                        onChange={(e) => setValues("PurchaseAmount", Array.isArray(e.value) ? e.value[0] : e.value)}
                        className="w-full mt-4"
                        min={0}
                        max={1500000}
                        step={1000}
                      />
                    </div>
                    <div className="w-[40%] flex px-2">
                      <REIInputNumber
                        id="purd-num-purchaseamount"
                        value={loanPurchaseCost?.PurchaseAmount}
                        onValueChange={(e) => setValues("PurchaseAmount", e.target.value)}
                        className="w-full"
                        useGrouping={true}
                        invalid={!!errors.PurchaseAmount}
                      />
                    </div>
                  </div>
                  {errors.PurchaseAmount && <div className="text-red-500 text-sm mt-1">{errors.PurchaseAmount}</div>}
                </div>
                <div className="flex-0 flex-col w-full lg:w-1/2 gap-2"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default PurchaseDetails;
